import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import _ from 'lodash';
import { deleteRule, fetchRanges, fetchRules, saveRanges, saveRules } from '../../../api';
import {
  disableForm,
  markDuplicateObjects,
  sortProductList,
  toFormRule,
  userHasAccess,
} from '../../../utils/helper';
import { accessPolicy, UserRoles } from '../../../constants';
import { OldSelect } from '../../styledComponents/Select/Select';
import ApplicableStates from './ApplicableStates';
import { useAppContext } from '../../../Store';
import { useDocEditFileQuery } from '../../../services/getDocEditFile';
import { useRulesQuery } from '../../../services/getRules';
import { useGetOrg } from '../../../services/getOrg';
import LoadingOverlay from 'react-loading-overlay-ts';
import { v4 as uuidv4 } from 'uuid';
import { Divider } from '@mui/material';

export enum UnitType {
  UNIT = 'unit',
  SUBUNIT = 'subunit',
  PRODUCT = 'product',
  LINE_OF_BUSINESS = 'lineOfBusiness',
}

export enum OtherType {
  BUSINESS_TYPE = 'businessType',
  IS_ACTIVE = 'isActive',
  COPY = 'copy',
  DELETE = 'delete',
}

export type Product = {
  unit: string;
  subunit: string;
  product: string;
  lineOfBusiness: string;
  businessType: boolean;
  rand: string;
  isActive: boolean;
  id: string;
  complete: boolean;
  duplicate: boolean;
  productIndex: number;
};

interface SingleProductProps {
  productIndex: number;
  unit: string;
  subunit: string;
  product: string;
  lineOfBusiness: string;
  indexId: string;
  isActive: boolean;
  businessType: boolean;
  complete: boolean;
  duplicate: boolean;
  formId: string;
  productHandler: (
    e: any,
    type: UnitType | OtherType,
    index: number,
    sortedIndex: number,
    product?: Product
  ) => void;
  disabled: boolean;
  userRoles: UserRoles[];
  rand: string;
  sortedIndex: number;
  businessUnitFilter: string;
}

const SingleProduct = memo((props: SingleProductProps) => {
  const {
    businessUnitFilter,
    productIndex,
    sortedIndex,
    indexId,
    formId,
    productHandler,
    disabled,
    userRoles,
    ...rest
  } = props;
  const [rangesError, setFetchRangesError] = useState<boolean>(false);
  const thisProduct: Product = {
    ...rest,
    ...{ id: indexId },
    productIndex,
  };

  const { data: orgData, isLoading: isOrgDataLoading } = useGetOrg();

  const items = [
    {
      title: 'Business Unit',
      type: UnitType.UNIT,
    },
    {
      title: 'Sub Business Unit',
      type: UnitType.SUBUNIT,
    },
    {
      title: 'Program',
      type: UnitType.PRODUCT,
    },
    {
      title: 'Line of Business',
      type: UnitType.LINE_OF_BUSINESS,
    },
  ];

  return (
    <>
      {!isOrgDataLoading &&
        (thisProduct.unit === businessUnitFilter ||
          thisProduct.unit === '' ||
          businessUnitFilter === 'ALL') && (
          <Box
            sx={{
              border: '2px solid',
              borderRadius: 1,
              borderColor: thisProduct.duplicate
                ? '#ff9800'
                : !thisProduct.complete
                ? 'red'
                : 'green',
              px: 1,
              pb: 1,
              pt: 2,
              mx: 'auto',
              my: 1,
              width: '100%',
            }}
          >
            <Stack direction="row" spacing={2}>
              {items.map((item, index) => {
                const selectValue = thisProduct[item.type];
                let selectOptions = [];
                let archived = false;

                if (orgData && orgData?.shapedHierarchyOptions) {
                  const hierarchy = orgData.shapedHierarchyOptions;
                  if (index === 0) {
                    selectOptions = Object.keys(hierarchy);
                    if (!hierarchy?.[thisProduct.unit]) {
                      archived = true;
                      selectOptions.push(selectValue);
                    }
                  }
                  if (index === 1 && thisProduct.unit) {
                    selectOptions = Object.keys(hierarchy?.[thisProduct.unit] || {});
                  }
                  if (index === 2 && thisProduct.subunit) {
                    selectOptions = Object.keys(
                      hierarchy?.[thisProduct.unit]?.[thisProduct.subunit] || {}
                    );
                  }
                  if (index === 3 && thisProduct.product) {
                    selectOptions =
                      hierarchy?.[thisProduct.unit]?.[thisProduct.subunit]?.[thisProduct.product] ||
                      [];
                  }
                }

                selectOptions.sort((a: string, b: string) =>
                  a.toUpperCase() < b.toUpperCase() ? -1 : 1
                );

                if (selectOptions.length === 0) archived = true;

                return (
                  <OldSelect
                    key={index}
                    id={item.title}
                    label={item.title}
                    value={selectValue || ''}
                    handleChange={(e) => {
                      productHandler(e, item.type, productIndex, sortedIndex);
                    }}
                    options={
                      selectOptions.length > 0
                        ? selectOptions.map((option: any) => {
                            return { value: option, label: option };
                          })
                        : [{ value: selectValue, label: selectValue }]
                    }
                    labelSize="small"
                    selectSize="small"
                    disabled={disabled || (index !== 0 && !thisProduct[items[index - 1].type])}
                    sx={{ color: archived ? '#ed6c02' : '' }}
                  />
                );
              })}
              <Stack direction="row">
                <Tooltip title="Delete">
                  <IconButton
                    color="primary"
                    aria-label="delete icon"
                    component="label"
                    disabled={disabled || !thisProduct.complete}
                    onClick={() =>
                      productHandler('', OtherType.DELETE, productIndex, sortedIndex, thisProduct)
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Make a copy of this product">
                  <IconButton
                    color="primary"
                    aria-label="copy icon"
                    component="label"
                    disabled={disabled || !thisProduct.complete}
                    onClick={() =>
                      productHandler('', OtherType.COPY, productIndex, sortedIndex, {
                        ...thisProduct,
                        ...{ id: '', rand: uuidv4(), duplicate: true },
                      })
                    }
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ padding: '8px 16px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={thisProduct.businessType}
                    onChange={(e) => {
                      productHandler(e, OtherType.BUSINESS_TYPE, productIndex, sortedIndex);
                    }}
                    disabled={disabled}
                    sx={{ padding: '8px' }}
                  />
                }
                label="Admitted"
              />
              <FormControlLabel
                disabled={disabled}
                labelPlacement="end"
                sx={{ alignSelf: 'flex-start' }}
                control={
                  <Switch
                    checked={thisProduct.isActive}
                    onChange={(e) => {
                      productHandler(e, OtherType.IS_ACTIVE, productIndex, sortedIndex);
                    }}
                  />
                }
                label={thisProduct.isActive ? 'Active' : 'Inactive'}
              />
            </Stack>
            {rangesError && (
              <Typography
                color="error"
                variant="body2"
                sx={{ textAlign: 'left', fontSize: 15, fontWeight: 700, mr: 'auto' }}
              >
                Error occurred while fetching state information.
              </Typography>
            )}
            {userHasAccess(accessPolicy.applicableStates.read, userRoles) && (
              <ApplicableStates
                formId={formId}
                indexId={thisProduct.id}
                index={productIndex}
                complete={thisProduct.complete}
                fetchRangesError={setFetchRangesError}
              />
            )}
          </Box>
        )}
    </>
  );
});

const emptyProduct = {
  unit: '',
  subunit: '',
  product: '',
  lineOfBusiness: '',
  businessType: true,
  rand: 'tempId',
  isActive: true,
  id: '',
  complete: false,
  duplicate: false,
};

type SelectedProductsProps = {
  formId: string;
  setIsProductsComplete: React.Dispatch<React.SetStateAction<boolean>>;
  isProductsComplete: boolean;
};

const SelectedProducts: React.FC<SelectedProductsProps> = (props) => {
  const { state } = useAppContext();
  const { formId, setIsProductsComplete, isProductsComplete } = props;
  const { data: docEditFileData } = useDocEditFileQuery(formId);
  const { data: rulesData, isLoading: isRulesLoading } = useRulesQuery(formId);
  const { data: orgData } = useGetOrg();
  const [productList, setProductList] = useState<Product[]>([]);
  const [copyIsLoading, setCopyIsLoading] = useState<boolean>(false);
  const [deleteIsLoading, setDeleteIsLoading] = useState<boolean>(false);
  const [businessUnitFilter, setBusinessUnitFilter] = useState<string>('ALL');
  const [businessUnitFilterOptions, setBusinessUnitFilterOptions] = useState<string[]>(['ALL']);
  const userRoles = state.auth.roles as UserRoles[];

  const productRef = useRef<null | HTMLDivElement>(null);

  const disabled = disableForm(docEditFileData?.data, state.auth);

  const saveProductOnChange = useCallback(
    async (productToSave: Product, index: number, statesData?: any) => {
      const formRule = toFormRule(index, productToSave);
      await saveRules([formRule], formId);
      if (statesData) {
        await saveRanges(formId, index, statesData).catch((err) => console.log(err));
      }
    },
    [formId]
  );

  const findDuplicates = (p: Product[]) => {
    return markDuplicateObjects(
      ['unit', 'subunit', 'product', 'lineOfBusiness', 'businessType'],
      p
    ) as Product[];
  };

  const handleSelect = useCallback(
    (e: any, type: UnitType, index: number, sortedIndex: number) => {
      setProductList((prevProductList) => {
        let newProductList = _.cloneDeep(prevProductList);

        newProductList[sortedIndex][type] = e.target.value;

        newProductList[sortedIndex].complete = false;
        if (type === UnitType.UNIT) {
          newProductList[sortedIndex].subunit = '';
          newProductList[sortedIndex].product = '';
          newProductList[sortedIndex].lineOfBusiness = '';
        } else if (type === UnitType.SUBUNIT) {
          newProductList[sortedIndex].product = '';
          newProductList[sortedIndex].lineOfBusiness = '';
        } else if (type === UnitType.PRODUCT) {
          newProductList[sortedIndex].lineOfBusiness = '';
        } else {
          newProductList[sortedIndex].complete = true;
        }

        if (newProductList[sortedIndex].complete)
          saveProductOnChange(newProductList[sortedIndex], index);

        let newProductListWithDuplicatesMarked = findDuplicates(newProductList);

        return newProductListWithDuplicatesMarked;
      });
    },
    [saveProductOnChange]
  );

  const handleCheckbox = useCallback(
    (e: any, index: number, sortedIndex: number) => {
      setProductList((prevProductList) => {
        let newProductList = prevProductList.map((product, i) => {
          if (i !== sortedIndex) return product;
          else {
            let newProduct = _.clone(product);
            newProduct.businessType = e.target.checked;
            if (newProduct.complete) saveProductOnChange(newProduct, index);
            return newProduct;
          }
        });
        let newProductListWithDuplicatesMarked = findDuplicates(newProductList);

        return newProductListWithDuplicatesMarked;
      });
    },
    [saveProductOnChange]
  );

  const handleToggle = useCallback(
    (e: any, index: number, sortedIndex: number) => {
      setProductList((prevProductList) =>
        prevProductList.map((product, i) => {
          if (i !== sortedIndex) return product;
          else {
            let newProduct = _.clone(product);
            newProduct.isActive = !newProduct.isActive;
            if (newProduct.complete) saveProductOnChange(newProduct, index);
            return newProduct;
          }
        })
      );
    },
    [saveProductOnChange]
  );

  const handleCopy = useCallback(
    async (index: number, sortedIndex: number, copy: Product) => {
      setCopyIsLoading(true);
      //save the state data to the new product in a temp field
      const applicableStates = await fetchRanges(formId, index);
      let data: any = [];
      if (applicableStates.data.ranges) data = Object.values(applicableStates.data.ranges);

      // this call to rules is a pain point.  Need to get the length of the product list at the time of copy in a way which
      // doesn't rerender every product
      const rules = await fetchRules(formId);

      const productListLength = rules.data.length;

      copy.productIndex = productListLength;
      await saveProductOnChange(copy, productListLength, data.length > 0 ? data : undefined);

      setProductList((prevState) => {
        let newProductList = _.cloneDeep(prevState);

        newProductList[sortedIndex].duplicate = true;

        // insert copy before the last product if that product was newly created and incomplete
        // this prevents the need to disable all copy buttons when one is in progress
        if (newProductList[newProductList.length - 1].unit === '') {
          newProductList.splice(newProductList.length - 1, 0, copy);
          return newProductList;
        } else return newProductList.concat([copy]);
      });

      setTimeout(() => scrollToLastProduct(), 1000);
      setCopyIsLoading(false);
    },
    [saveProductOnChange, formId]
  );

  const handleDelete = useCallback(
    async (index: number, sortedIndex: number, product: Product) => {
      setProductList((prevState) => {
        const newProductList = _.cloneDeep(prevState);
        newProductList.splice(sortedIndex, 1);

        // Subtract 1 from productIndex for each item whose index is greater than the deleted product's index
        // Since db sort and frontend sort are decoupled this becomes manual
        const adjustedList = newProductList.map((p) => {
          if (p.productIndex > index) {
            return {
              ...p,
              productIndex: p.productIndex - 1,
            };
          }
          return p;
        });

        let newProductListWithDuplicatesMarked = findDuplicates(adjustedList);
        return newProductListWithDuplicatesMarked;
      });

      // deletes from the top of the list are the most expensive as they rerender every product below.
      // every product belows prop "productIndex" changes because all of the products below shift their index by 1
      if (product.complete) {
        setDeleteIsLoading(true);
        await deleteRule(formId ?? '', index);
        setDeleteIsLoading(false);
      }
    },
    [formId]
  );

  const productHandler = useCallback(
    (e: any, type: UnitType | OtherType, index: number, sortedIndex: number, product?: Product) => {
      if (type === OtherType.BUSINESS_TYPE) {
        handleCheckbox(e, index, sortedIndex);
      } else if (type === OtherType.IS_ACTIVE) {
        handleToggle(e, index, sortedIndex);
      } else if (type === OtherType.COPY && product) {
        handleCopy(index, sortedIndex, product);
      } else if (type === OtherType.DELETE && product) {
        handleDelete(index, sortedIndex, product);
      } else handleSelect(e, type as UnitType, index, sortedIndex);
    },
    [handleCheckbox, handleSelect, handleToggle, handleCopy, handleDelete]
  );

  useEffect(() => {
    if (productList.length === 0) {
      setIsProductsComplete(false);
    } else if (productList.length > 0 && productList.some((product) => !product.complete)) {
      setIsProductsComplete(false);
    } else {
      setIsProductsComplete(true);
    }

    if (!productList.find((p) => p.unit === businessUnitFilter) && businessUnitFilter !== 'ALL') {
      setBusinessUnitFilter('ALL');
    }
    setBusinessUnitFilterOptions(
      ['ALL']
        .concat(Object.keys(orgData?.shapedHierarchyOptions))
        .filter((unit) => productList.find((p) => p.unit === unit) || unit === 'ALL')
    );
  }, [productList, setIsProductsComplete, businessUnitFilter, orgData?.shapedHierarchyOptions]);

  useEffect(() => {
    if (isRulesLoading) return;
    let products = rulesData.data.map((rule: any) => ({
      productIndex: rule[0],
      unit: rule[1].unit,
      subunit: rule[1].subunit,
      product: rule[1].product,
      lineOfBusiness: rule[1].lineOfBusiness,
      businessType: rule[1].businessType === 'Admitted' ? true : false,
      complete: true,
      rand: uuidv4(),
      ...rule[2],
    }));
    let newProductListWithDuplicatesMarked: Product[] = findDuplicates(products);
    //only frontend sort on load
    setProductList(sortProductList(newProductListWithDuplicatesMarked));
  }, [rulesData, isRulesLoading]);

  const handleAdd = () => {
    setProductList((prevState) =>
      prevState.concat([{ ...emptyProduct, rand: uuidv4(), productIndex: prevState.length }])
    );
    setTimeout(() => scrollToLastProduct(), 500);
  };

  const scrollToLastProduct = () => {
    const lastChildElement = productRef.current?.lastElementChild;
    lastChildElement?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToFirstProduct = () => {
    const firstElement = productRef.current?.firstElementChild as HTMLElement | null;

    if (firstElement) {
      const rect = firstElement.getBoundingClientRect();
      const offsetTop = window.scrollY + rect.top - 89;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {formId && (
        <Stack
          spacing={2}
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 10,
            py: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" width={'100%'} spacing={12}>
            <Typography sx={{ textAlign: 'left', fontSize: 24, fontWeight: 700, mr: 'auto' }}>
              Products
            </Typography>
            <OldSelect
              id={'bu-filter'}
              label={'Filter By'}
              value={businessUnitFilter}
              handleChange={(e) => {
                setBusinessUnitFilter(e.target.value);
                scrollToFirstProduct();
              }}
              options={businessUnitFilterOptions.map((option: any) => ({
                value: option,
                label: option,
              }))}
              labelSize="small"
              selectSize="small"
            />
            <Stack direction={'row'} sx={{ pr: '8px' }}>
              <Tooltip title="Add Product">
                <IconButton
                  color="primary"
                  aria-label="Add icon"
                  component="label"
                  size="medium"
                  onClick={handleAdd}
                  disabled={
                    disabled ? true : productList.length === 0 ? false : !isProductsComplete
                  }
                >
                  <AddIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Scroll Top">
                <IconButton
                  color="primary"
                  aria-label="scroll icon"
                  component="label"
                  size="medium"
                  onClick={() => scrollToFirstProduct()}
                >
                  <KeyboardDoubleArrowUpIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Divider />
        </Stack>
      )}
      <Stack sx={{ overflow: 'auto', minHeight: '181px' }}>
        <LoadingOverlay
          ref={productRef}
          active={copyIsLoading || deleteIsLoading}
          styles={{
            overlay: (base) => ({
              ...base,
              background: 'rgba(0, 0, 0, 0.26)',
            }),
            spinner: {},
          }}
        >
          {productList.map((p: Product, index: number) => {
            // need to pass props at string/number level so Object.is evaluates to true for unchanged products
            return (
              <SingleProduct
                key={p.rand}
                rand={p.rand}
                productIndex={p.productIndex} // DB sort order
                sortedIndex={index} // frontend sort order
                unit={p.unit}
                subunit={p.subunit}
                product={p.product}
                lineOfBusiness={p.lineOfBusiness}
                indexId={p.id}
                businessType={p.businessType}
                isActive={p.isActive}
                complete={p.complete}
                duplicate={p.duplicate}
                formId={formId}
                productHandler={productHandler}
                disabled={disabled}
                userRoles={userRoles}
                businessUnitFilter={businessUnitFilter}
              />
            );
          })}
        </LoadingOverlay>
      </Stack>
    </>
  );
};

export default SelectedProducts;
